import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { cn } from '@mdvip/shared/lib/utils';
import type * as React from 'react';

function Pagination({ className, ...props }: React.ComponentProps<'nav'>) {
  return (
    <nav
      aria-label="Pagination"
      data-slot="pagination"
      className={cn('@container mx-auto flex w-full justify-center', className)}
      {...props}
    />
  );
}

function PaginationContent({ className, ...props }: React.ComponentProps<'ul'>) {
  return <ul data-slot="pagination-content" className={cn('flex flex-row items-center gap-1', className)} {...props} />;
}

function PaginationItem({ ...props }: React.ComponentProps<'li'>) {
  return <li data-slot="pagination-item" {...props} />;
}

interface PaginationLinkProps extends React.ComponentProps<'a'> {
  isActive?: boolean;
}

const LINK_BASE =
  'inline-flex size-8 items-center justify-center gap-2 rounded-[4px] text-sm font-medium transition-colors duration-fast ease-out outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

function PaginationLink({ className, isActive, ...props }: PaginationLinkProps) {
  return (
    <a
      aria-current={isActive ? 'page' : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      className={cn(
        LINK_BASE,
        isActive
          ? 'bg-primary text-primary-foreground shadow-xs'
          : 'text-foreground hover:bg-accent hover:text-accent-foreground',
        className,
      )}
      {...props}
    />
  );
}

interface PaginationNavProps extends PaginationLinkProps {
  disabled?: boolean;
  /** Visible label text. Override to localize — `aria-label` is separately overridable via the standard `aria-label` prop. */
  label?: string;
}

function PaginationPrevious({ className, disabled, label = 'Previous', ...props }: PaginationNavProps) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      className={cn('w-auto gap-1 px-3', disabled && 'pointer-events-none opacity-50', className)}
      {...props}
    >
      <Icon name="chevronLeft" size="md" />
      <span className="hidden @[560px]:inline">{label}</span>
    </PaginationLink>
  );
}

function PaginationNext({ className, disabled, label = 'Next', ...props }: PaginationNavProps) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      className={cn('w-auto gap-1 px-3', disabled && 'pointer-events-none opacity-50', className)}
      {...props}
    >
      <span className="hidden @[560px]:inline">{label}</span>
      <Icon name="chevronRight" size="md" />
    </PaginationLink>
  );
}

function PaginationEllipsis({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      aria-hidden="true"
      data-slot="pagination-ellipsis"
      className={cn('flex size-8 items-center justify-center text-muted-foreground', className)}
      {...props}
    >
      {'…'}
      <span className="sr-only">{'More pages'}</span>
    </span>
  );
}

/** A page target in a windowed range: a real page number, or a gap collapsed behind an ellipsis. */
export type PaginationRangeItem = number | 'start-ellipsis' | 'end-ellipsis';

/**
 * Windows a page range around the current page (e.g. `1 … 4 5 6 … 20`) so a listing with hundreds
 * of pages doesn't render a link per page. `siblings` controls how many pages flank the current one
 * on each side.
 */
function paginationRange(page: number, pageCount: number, siblings = 1): PaginationRangeItem[] {
  const totalVisible = siblings * 2 + 5;
  if (pageCount <= totalVisible) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  const left = Math.max(page - siblings, 1);
  const right = Math.min(page + siblings, pageCount);
  const showLeftEllipsis = left > 2;
  const showRightEllipsis = right < pageCount - 1;

  const range: PaginationRangeItem[] = [1];
  if (showLeftEllipsis) range.push('start-ellipsis');
  for (let p = Math.max(2, left); p <= Math.min(pageCount - 1, right); p++) range.push(p);
  if (showRightEllipsis) range.push('end-ellipsis');
  range.push(pageCount);
  return range;
}

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  paginationRange,
};
